import { useQuery } from "@tanstack/react-query";
import { createContext, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";

import type { Finca, SesionAuthYo } from "../modules/cuenta/tipos";
import { fetchAutenticado, useEstaAutenticado } from "./autenticacion";

/**
 * NOTA: el cliente congelado de src/api/client.ts combina baseUrl "/api/v1"
 * con claves de `paths` que YA incluyen el prefijo "/api/v1" (así las generó
 * openapi-typescript desde contracts/openapi.yaml), lo que duplica la URL
 * en tiempo de ejecución (p.ej. "/api/v1/api/v1/auth/yo"). No es una ruta
 * que F06 posea (api/**, congelado) — se documenta acá como candidato a CR
 * y, mientras tanto, esta sesión usa fetch() directo (MSW intercepta igual).
 */

interface ContextoSesionValor {
  sesion: SesionAuthYo | undefined;
  cargando: boolean;
  fincaActiva: Finca | undefined;
  establecerFincaActiva: (id: string) => void;
}

const ContextoSesion = createContext<ContextoSesionValor | null>(null);

/**
 * Sesión + finca activa: el único estado global de la app (§2.7). Pide
 * GET /auth/yo (mockeado por MSW) con staleTime 30s, solo si hay token.
 */
export function ProveedorSesion({ children }: { children: ReactNode }) {
  const autenticado = useEstaAutenticado();
  const [fincaActivaId, setFincaActivaId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["auth", "yo"],
    queryFn: async () => {
      const respuesta = await fetchAutenticado("/api/v1/auth/yo");
      if (!respuesta.ok) throw new Error("No se pudo obtener la sesión");
      const yo = (await respuesta.json()) as Record<string, unknown>;
      // Compatibilidad con la respuesta real de F03 y con el mock provisional de F06.
      if (!("membresia_activa" in yo)) return yo as unknown as SesionAuthYo;
      const activa = yo.membresia_activa as { id: string; rol: SesionAuthYo["rol"]; cuenta: { nombre: string } };
      const fincasRespuesta = await fetchAutenticado("/api/v1/fincas/");
      const pagina = fincasRespuesta.ok ? await fincasRespuesta.json() as { resultados?: Finca[] } : {};
      const fincas = pagina.resultados ?? [];
      const usuario = yo.usuario as { id: string; nombre: string; email?: string };
      return {
        usuario: { id: usuario.id, nombre: usuario.nombre, email: usuario.email ?? "" },
        rol: activa.rol,
        fincas,
        fincaActivaId: fincas[0]?.id ?? "",
        cuenta: { estado: "activa" },
        membresiaActiva: { id: activa.id, cuenta: activa.cuenta.nombre, rol: activa.rol },
        otrasMembresias: (yo.otras_membresias as { id: string; cuenta: string; rol: SesionAuthYo["rol"] }[]).map((m) => ({ ...m })),
      } satisfies SesionAuthYo;
    },
    enabled: autenticado,
    staleTime: 30_000,
  });

  const fincaActiva = useMemo(() => {
    if (!data) return undefined;
    const id = fincaActivaId ?? data.fincaActivaId;
    return data.fincas.find((f) => f.id === id) ?? data.fincas[0];
  }, [data, fincaActivaId]);

  const valor = useMemo<ContextoSesionValor>(
    () => ({
      sesion: data,
      cargando: autenticado && isLoading,
      fincaActiva,
      establecerFincaActiva: setFincaActivaId,
    }),
    [data, autenticado, isLoading, fincaActiva],
  );

  return <ContextoSesion.Provider value={valor}>{children}</ContextoSesion.Provider>;
}

export function useSesion(): ContextoSesionValor {
  const contexto = useContext(ContextoSesion);
  if (!contexto) {
    throw new Error("useSesion debe usarse dentro de <ProveedorSesion>");
  }
  return contexto;
}
